import React from "react";
import { Modal, Button, Tag } from "antd";
import {
  InfoCircleOutlined,
  CloseOutlined,
  TeamOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import welcomeStyles from "pages/Welcome/style.module.scss";
import {
  resolveWidgetInfoDisplayName,
  resolveWidgetInfoMetricTypeName,
} from "pages/Welcome/welcomeWidgetInfoLabels";
import {
  formatWelcomePivotFilterBooleanExpr,
  resolveWelcomeWidgetPivotFilter,
} from "pages/Welcome/welcomePivotBooleanLogic";

import { formatAppDate, formatAppDateTime } from "util/formatAppDate";

function formatWidgetInfoDate(iso, language) {
  if (iso == null || iso === "") return null;
  const formatted = formatAppDate(iso, language);
  return formatted || String(iso);
}

function formatWidgetInfoDateTime(iso, language) {
  if (iso == null || iso === "") return null;
  const formatted = formatAppDateTime(iso, language, { useDayjs: false });
  return formatted || String(iso);
}

function formatInfoList(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.join(", ");
  }
  return null;
}

function resolveWidgetInfoDashboardContextLabel(widgetInfo, sessionDashboardName) {
  const session = String(sessionDashboardName ?? "").trim();
  const ws = String(widgetInfo?.welcome_group_name ?? "").trim();
  if (session && ws) return `${session} · ${ws}`;
  if (session) return session;
  if (ws) return ws;
  return formatInfoList(widgetInfo?.dashboards) || "";
}

/** Same modal body as My Workspace (Welcome). */
export function WelcomeWidgetInfoModal({ open, widgetInfo, widgetRow, onClose }) {
  const { t, i18n } = useTranslation();
  const sessionDashboardName = String(
    useSelector((state) => state.DashboardReducer?.dashboard?.Name) ?? "",
  ).trim();
  const dashboardContextLabel = resolveWidgetInfoDashboardContextLabel(
    widgetInfo,
    sessionDashboardName,
  );

  const renderJobStatusTag = (status) => {
    if (status === "not_found" || status == null) {
      return <Tag>{t("welcome.infoJobNotFound")}</Tag>;
    }
    const n = Number(status);
    const map = {
      1: { color: "default", label: t("welcome.infoJobPending") },
      2: { color: "processing", label: t("welcome.infoJobInProgress") },
      3: { color: "success", label: t("welcome.infoJobReady") },
      4: { color: "error", label: t("welcome.infoJobFailed") },
    };
    const cfg = map[n];
    if (!cfg) {
      return <Tag>{String(status)}</Tag>;
    }
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  return (
    <Modal
      title={null}
      className={welcomeStyles.widgetInfoPopupModal}
      open={open}
      onCancel={onClose}
      width={800}
      destroyOnClose
      closeIcon={null}
      footer={
        <Button
          type="primary"
          className={welcomeStyles.widgetInfoFooterClose}
          onClick={onClose}
        >
          {t("welcome.infoClose")}
        </Button>
      }
    >
      {widgetInfo ? (
        <div className={welcomeStyles.widgetInfoModalContent}>
          <div className={welcomeStyles.widgetInfoTitleBox}>
            <span className={welcomeStyles.widgetInfoTitleLeft}>
              <InfoCircleOutlined className={welcomeStyles.widgetInfoTitleIcon} />
              <span>{t("welcome.widgetInfoTitle")}</span>
            </span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              className={welcomeStyles.widgetInfoTitleClose}
              onClick={onClose}
            />
          </div>

          <div className={welcomeStyles.widgetInfoBelowContent}>
            <div className={welcomeStyles.widgetInfoColumns}>
              <div className={welcomeStyles.widgetInfoColumn}>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <TeamOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoName")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {resolveWidgetInfoDisplayName(widgetInfo, t, widgetRow)}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <FileTextOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoRepresentationType")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {widgetInfo["Representation Type"] ?? "—"}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <ThunderboltOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoJobLabel")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {renderJobStatusTag(widgetInfo.job_status)}
                  </span>
                </div>

                <div
                  className={`${welcomeStyles.widgetInfoSection} ${welcomeStyles.widgetInfoSectionInColumn}`}
                >
                  <h4 className={welcomeStyles.widgetInfoSectionTitle}>
                    {t("welcome.infoSectionDateRange")}
                  </h4>
                  {widgetInfo.since || widgetInfo.until ? (
                    <div className={welcomeStyles.widgetInfoDateRangeBox}>
                      <div className={welcomeStyles.widgetInfoDateRangeItem}>
                        <span className={welcomeStyles.widgetInfoDateRangeLabel}>
                          <CalendarOutlined
                            className={welcomeStyles.widgetInfoDateRangeLabelIcon}
                            aria-hidden
                          />
                          {t("welcome.infoDateStart")}
                        </span>
                        <span className={welcomeStyles.widgetInfoDateRangeValue}>
                          {widgetInfo.since
                            ? formatWidgetInfoDate(widgetInfo.since, i18n.language)
                            : "—"}
                        </span>
                      </div>
                      <div className={welcomeStyles.widgetInfoDateRangeItem}>
                        <span className={welcomeStyles.widgetInfoDateRangeLabel}>
                          <CalendarOutlined
                            className={welcomeStyles.widgetInfoDateRangeLabelIcon}
                            aria-hidden
                          />
                          {t("welcome.infoDateEnd")}
                        </span>
                        <span className={welcomeStyles.widgetInfoDateRangeValue}>
                          {widgetInfo.until
                            ? formatWidgetInfoDate(widgetInfo.until, i18n.language)
                            : "—"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span className={welcomeStyles.widgetInfoMutedText}>
                      {t("welcome.infoNoDateStored")}
                    </span>
                  )}
                </div>

                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <TeamOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoBrands")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {formatInfoList(widgetInfo.brands) ?? t("welcome.infoNoSources")}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <UnorderedListOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoTopics")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {formatInfoList(widgetInfo.Topics) ?? t("welcome.infoNoTopics")}
                  </span>
                </div>

                <div
                  className={`${welcomeStyles.widgetInfoSection} ${welcomeStyles.widgetInfoSectionInColumn}`}
                >
                  <h4 className={welcomeStyles.widgetInfoSectionTitle}>
                    {t("welcome.infoSectionPivot")}
                  </h4>
                  {(() => {
                    const rowPivotFilter = resolveWelcomeWidgetPivotFilter(
                      widgetRow,
                      widgetInfo,
                    );
                    const booleanExpr =
                      formatWelcomePivotFilterBooleanExpr(rowPivotFilter);
                    if (!booleanExpr) {
                      return (
                        <span className={welcomeStyles.widgetInfoMutedText}>
                          {t("welcome.infoNoPivot")}
                        </span>
                      );
                    }
                    return (
                      <>
                        <div className={welcomeStyles.widgetInfoPivotBooleanLabel}>
                          {t("welcome.pivotFilterBooleanLogic").toUpperCase()}
                        </div>
                        <div className={welcomeStyles.widgetInfoPivotBooleanExpr}>
                          {booleanExpr}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className={welcomeStyles.widgetInfoColumn}>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <FileTextOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoWidgetType")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {resolveWidgetInfoMetricTypeName(widgetInfo, t, widgetRow)}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <DashboardOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoMetricType")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {widgetInfo["metric Type"] ?? "—"}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <ClockCircleOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoCreatedAt")}
                  </span>
                  <span
                    className={welcomeStyles.widgetInfoItemValue}
                    title={
                      widgetInfo.created_at
                        ? undefined
                        : t("welcome.infoCreatedUnknown")
                    }
                  >
                    {widgetInfo.created_at
                      ? formatWidgetInfoDateTime(widgetInfo.created_at, i18n.language)
                      : "—"}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <DashboardOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoDashboards")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {formatInfoList(widgetInfo.dashboards) ?? t("welcome.infoNoSources")}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <DashboardOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("ai_services.reports_detail_dashboard_label")}
                  </span>
                  <span
                    className={welcomeStyles.widgetInfoItemValue}
                    title={
                      dashboardContextLabel
                        ? undefined
                        : t("ai_services.reports_session_dashboard_none")
                    }
                  >
                    {dashboardContextLabel || "—"}
                  </span>
                </div>
                <div className={welcomeStyles.widgetInfoItem}>
                  <span className={welcomeStyles.widgetInfoItemLabel}>
                    <DatabaseOutlined className={welcomeStyles.widgetInfoItemIcon} />
                    {t("welcome.infoNumReviews")}
                  </span>
                  <span className={welcomeStyles.widgetInfoItemValue}>
                    {widgetInfo.review_count_total != null &&
                    widgetInfo.review_count_total !== ""
                      ? t("welcome.infoNumReviewsTotal", {
                          count: widgetInfo.review_count_total,
                        })
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {(widgetInfo.widget_id != null || widgetRow?.id != null) && (
            <div className={welcomeStyles.widgetInfoWidgetId}>
              {t("welcome.infoWidgetId", {
                id: widgetInfo.widget_id ?? widgetRow?.id,
              })}
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
